import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./Admin.css";
import "./Gallery.admin.css";
import { API_BASE_URL } from "../../config";

const convertToWebP = (file) =>
    new Promise((resolve) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            canvas.getContext("2d").drawImage(img, 0, 0);
            canvas.toBlob(
                (blob) => {
                    URL.revokeObjectURL(url);
                    resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".webp"), { type: "image/webp" }));
                },
                "image/webp",
                0.85
            );
        };
        img.src = url;
    });

const uploadToUT = async (file) => {
    const formData = new FormData();
    formData.append("files", file);
    const res = await fetch(`${API_BASE_URL}/api/uploadthing`, { method: "POST", body: formData });
    if (!res.ok) throw new Error("Upload failed");
    const data = await res.json();
    const item = Array.isArray(data) ? data[0] : data;
    return { url: item.url || item.ufsUrl, key: item.key };
};

const AdminGalleryFolder = () => {
    const { folderId } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef();
    const [folder, setFolder] = useState(null);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState("");
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [lightboxIdx, setLightboxIdx] = useState(null);

    useEffect(() => {
        const token = localStorage.getItem("admin_token");
        if (!token) { navigate("/admin/anubhava-montessori/qwerzxcv6909/login"); return; }
        fetchAll();
    }, [folderId]);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [folderRes, imagesRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/gallery/folders/${folderId}`),
                fetch(`${API_BASE_URL}/api/gallery/folders/${folderId}/images`),
            ]);
            const folderData = await folderRes.json();
            const imagesData = await imagesRes.json();
            setFolder(folderData);
            setImages(Array.isArray(imagesData) ? imagesData : []);
        } catch { }
        setLoading(false);
    };

    const handleFilesSelected = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setUploading(true);
        const token = localStorage.getItem("admin_token");
        let successCount = 0;

        for (let i = 0; i < files.length; i++) {
            try {
                setUploadStatus(`Processing ${i + 1}/${files.length}: Converting to WebP...`);
                const webp = await convertToWebP(files[i]);
                setUploadStatus(`Uploading ${i + 1}/${files.length}...`);
                const { url, key } = await uploadToUT(webp);
                await fetch(`${API_BASE_URL}/api/gallery/folders/${folderId}/images`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ url, key, caption: "" }),
                });
                successCount++;
            } catch (err) {
                console.error("Upload error for file:", files[i].name, err);
            }
        }
        setUploadStatus(`Done! ${successCount}/${files.length} images uploaded.`);
        setTimeout(() => setUploadStatus(""), 3000);
        setUploading(false);
        fileInputRef.current.value = "";
        fetchAll();
    };

    const handleDeleteImage = async (id) => {
        const token = localStorage.getItem("admin_token");
        try {
            const res = await fetch(`${API_BASE_URL}/api/gallery/images/${id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                setImages((prev) => prev.filter((img) => img._id !== id));
                setDeleteConfirmId(null);
            }
        } catch { alert("Error deleting image."); }
    };

    // Keyboard navigation for lightbox
    useEffect(() => {
        const handler = (e) => {
            if (lightboxIdx === null) return;
            if (e.key === "ArrowRight") setLightboxIdx((i) => Math.min(i + 1, images.length - 1));
            if (e.key === "ArrowLeft") setLightboxIdx((i) => Math.max(i - 1, 0));
            if (e.key === "Escape") setLightboxIdx(null);
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [lightboxIdx, images.length]);

    return (
        <div className="dashboard-container">
            {/* Sidebar */}
            <div className="dashboard-sidebar">
                <div className="sidebar-logo">Anubhava</div>
                <ul className="sidebar-menu">
                    <li className="sidebar-item" onClick={() => navigate("/admin/anubhava-montessori/qwerzxcv6909/blogs")}>
                        <i className="fa-solid fa-blog"></i><span>Manage Blogs</span>
                    </li>
                    <li className="sidebar-item" onClick={() => navigate("/admin/anubhava-montessori/qwerzxcv6909/students")}>
                        <i className="fa-solid fa-users"></i><span>Students CRM</span>
                    </li>
                    <li className="sidebar-item active">
                        <i className="fa-solid fa-images"></i><span>Gallery</span>
                    </li>
                    <li className="sidebar-item" onClick={() => navigate("/gallery")}>
                        <i className="fa-solid fa-globe"></i><span>Visit Website</span>
                    </li>
                </ul>
                <div className="sidebar-logout">
                    <button onClick={() => { localStorage.removeItem("admin_token"); navigate("/admin/anubhava-montessori/qwerzxcv6909/login"); }} className="btn-secondary" style={{ width: "100%" }}>Log Out</button>
                </div>
            </div>

            {/* Main */}
            <div className="dashboard-content">
                <div className="dashboard-header">
                    <div>
                        <button className="back-btn" onClick={() => navigate("/admin/anubhava-montessori/qwerzxcv6909/gallery")}>
                            <i className="fa-solid fa-arrow-left"></i> All Folders
                        </button>
                        <h1 style={{ marginTop: "8px" }}>{loading ? "Loading..." : folder?.name}</h1>
                        <p style={{ color: "#777", fontSize: "15px", marginTop: "4px" }}>
                            {images.length} image{images.length !== 1 ? "s" : ""}
                            {folder?.displayDate && ` · ${folder.displayDate}`}
                        </p>
                    </div>
                    <button className="new-blog-btn" onClick={() => fileInputRef.current.click()} disabled={uploading}>
                        <i className="fa-solid fa-upload"></i> {uploading ? "Uploading..." : "Upload Images"}
                    </button>
                </div>

                {/* Hidden file input (multi) */}
                <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleFilesSelected} />

                {/* Upload status */}
                {uploadStatus && (
                    <div className="upload-status-bar">
                        <i className="fa-solid fa-spinner fa-spin"></i> {uploadStatus}
                    </div>
                )}

                {loading ? (
                    <div className="students-loading">Loading images...</div>
                ) : images.length === 0 ? (
                    <div className="students-empty">
                        <i className="fa-solid fa-image" style={{ fontSize: "56px", color: "#ddd", marginBottom: "16px" }}></i>
                        <h3>No images in this folder</h3>
                        <p>Click <strong>Upload Images</strong> to add photos.</p>
                    </div>
                ) : (
                    <div className="admin-image-grid">
                        {images.map((img, idx) => (
                            <div key={img._id} className="admin-image-cell">
                                <img src={img.url} alt={img.caption || `Image ${idx + 1}`} onClick={() => setLightboxIdx(idx)} />
                                <div className="admin-image-overlay">
                                    <button className="img-view-btn" onClick={() => setLightboxIdx(idx)}>
                                        <i className="fa-solid fa-expand"></i>
                                    </button>
                                    {deleteConfirmId === img._id ? (
                                        <div className="img-delete-confirm">
                                            <button className="confirm-yes-btn" onClick={() => handleDeleteImage(img._id)}>Delete</button>
                                            <button className="confirm-no-btn" onClick={() => setDeleteConfirmId(null)}>Cancel</button>
                                        </div>
                                    ) : (
                                        <button className="img-delete-btn" onClick={() => setDeleteConfirmId(img._id)}>
                                            <i className="fa-solid fa-trash-can"></i>
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Lightbox */}
            {lightboxIdx !== null && (
                <div className="admin-lightbox" onClick={() => setLightboxIdx(null)}>
                    <button className="lb-close" onClick={() => setLightboxIdx(null)}><i className="fa-solid fa-xmark"></i></button>
                    {lightboxIdx > 0 && (
                        <button className="lb-prev" onClick={(e) => { e.stopPropagation(); setLightboxIdx(lightboxIdx - 1); }}>
                            <i className="fa-solid fa-chevron-left"></i>
                        </button>
                    )}
                    <img src={images[lightboxIdx]?.url} alt="" onClick={(e) => e.stopPropagation()} />
                    {lightboxIdx < images.length - 1 && (
                        <button className="lb-next" onClick={(e) => { e.stopPropagation(); setLightboxIdx(lightboxIdx + 1); }}>
                            <i className="fa-solid fa-chevron-right"></i>
                        </button>
                    )}
                    <div className="lb-counter">{lightboxIdx + 1} / {images.length}</div>
                </div>
            )}
        </div>
    );
};

export default AdminGalleryFolder;
